/**
 * Setzt die Style-Tokens (CSS-Variablen) fuer die Anmeldeanzeige moeglichst frueh,
 * damit Skeleton/Karten sofort im Vereins-Design erscheinen. Portiert aus
 * setLayoutSettings()/changeColor() in registrationApp.js (idempotent; der Monolith
 * setzt dieselben Variablen beim Boot erneut).
 */

export interface RegistrationStyleData {
    writeColorParagraph?: string;
    writeColorHeader?: string;
    fontFamilyParagraph?: string;
    fontFamilyHeader?: string;
    highlightColor?: string;
    buttonWriteColor?: string;
    backgroundColor?: string;
    cardColor?: string;
    spaceTop?: number | string;
    spaceBottom?: number | string;
    spaceRight?: number | string;
    spaceLeft?: number | string;
}

function changeColor(color: string, amount: number): string {
    const clamp = (val: number): number => Math.min(Math.max(val, 0), 0xff);
    const fill = (str: string): string => `00${str}`.slice(-2);
    const num = parseInt(color.substr(1), 16);
    const red = clamp((num >> 16) + amount);
    const green = clamp(((num >> 8) & 0x00ff) + amount);
    const blue = clamp((num & 0x0000ff) + amount);
    return `#${fill(red.toString(16))}${fill(green.toString(16))}${fill(blue.toString(16))}`;
}

let applied = false;

export function applyRegistrationStyleTokens(styleInput: RegistrationStyleData | null | undefined): void {
    if (typeof document === 'undefined') return;
    const style: RegistrationStyleData = styleInput || {};
    const root = document.documentElement;
    const set = (name: string, value: string): void => root.style.setProperty(name, value);
    const setColor = (name: string, value: string | undefined, fallback: string): void => {
        set(name, value != null && value !== '' ? String(value) : fallback);
    };

    setColor('--writeColorParagraph', style.writeColorParagraph, '#6d6e70');
    setColor('--writeColorHeader', style.writeColorHeader, '#323C4E');
    setColor('--fontFamilyParagraph', style.fontFamilyParagraph, 'Arial');
    setColor('--fontFamilyHeader', style.fontFamilyHeader, 'Helvetica');

    set('--writeColorParagraph2', '#999aa0');
    set('--writeColorParagraph3', '#6d6e70');
    set('--secondaryColor', '#f1f2f6');
    set('--secondaryHoverColor', '#dddddd');

    const highlight = style.highlightColor != null && style.highlightColor !== '' ? String(style.highlightColor) : '#6499E9';
    set('--highlightColor', highlight);
    try {
        set('--highlightHoverColor', changeColor(highlight, -20));
        set('--highlightColorLight', changeColor(highlight, 30));
        set('--highlightColorLight2', changeColor(highlight, 60));
    } catch {
        /* ungueltiger Farbwert -> Monolith setzt spaeter korrekt */
    }

    set('--deniedColor', '#d86ab3');
    set('--notSelectable', '#f1f2f6');
    setColor('--buttonWriteColor', style.buttonWriteColor, '#ffffffea');
    setColor('--backgroundColor', style.backgroundColor, 'transparent');
    set('--backgroundColorTransparent', '#fffffff4');
    setColor('--cardColor', style.cardColor, '#ffffff');

    const setSpace = (name: string, value: number | string | undefined, fallback: string): void => {
        if (value != null && value !== '' && !Number.isNaN(Number(value))) {
            set(name, `${value}px`);
        } else {
            set(name, fallback);
        }
    };
    setSpace('--spaceTop', style.spaceTop, '20px');
    setSpace('--spaceBottom', style.spaceBottom, '20px');
    setSpace('--spaceRight', style.spaceRight, '20px');
    setSpace('--spaceLeft', style.spaceLeft, '20px');

    applied = true;
}

export function registrationStyleTokensApplied(): boolean {
    return applied;
}
